/**
 * Fake attribute pickers (Diamond Shape / Metal) — filter product gallery by media alt.
 * Alt format: "Round,Yellow Gold" (comma-separated tokens).
 * AIMINIA STEP 2: initialize shape/metal from the clicked product link.
 */
(() => {
  const states = new Map();

  // AIMINIA STEP 2: capture the entry link before another theme script changes it.
  // Example from the collection-card patch: ?shape=pear&metal=white-gold
  // This script does not change the native variant ID, price or product title.
  const entryHref = window.location.href;
  const entrySelection = (() => {
    try {
      const url = new URL(entryHref);
      return {
        shape: url.searchParams.get('shape') || '',
        metal: url.searchParams.get('metal') || '',
      };
    } catch (error) {
      return { shape: '', metal: '' };
    }
  })();

  function getProductHandle(value) {
    if (!value) return '';
    try {
      const pathname = new URL(value, entryHref).pathname;
      const match = pathname.match(/\/products\/([^/]+)/);
      return match ? match[1] : '';
    } catch (error) {
      return '';
    }
  }

  const entryProductHandle = getProductHandle(entryHref);

  function getPickerProductHandle(picker) {
    // Do not pass the main product's URL selection into another product's picker.
    const productInfo = picker.closest('product-info[data-url]');
    const handle = getProductHandle(productInfo?.dataset.url || '');
    if (handle) return handle;

    const mainProduct = document.getElementById(`MainProduct-${picker.dataset.sectionId}`);
    return mainProduct?.contains(picker) ? entryProductHandle : '';
  }

  function getState(sectionId) {
    if (!states.has(sectionId)) {
      states.set(sectionId, { shape: '', metal: '', productKey: '' });
    }
    return states.get(sectionId);
  }

  function normalize(value) {
    // Match URL slugs (white-gold) to existing button/Alt values (White Gold).
    // Keep the actual button value unchanged when submitting cart properties.
    return String(value || '').trim().toLowerCase().replace(/[-_\s]+/g, ' ');
  }

  function altMatches(alt, shape, metal) {
    if (!alt) return false;
    const parts = String(alt)
      .split(/[,，]/)
      .map((part) => normalize(part))
      .filter(Boolean);

    if (!parts.length) return false;

    const shapeNorm = normalize(shape);
    const metalNorm = normalize(metal);

    if (shapeNorm && !parts.includes(shapeNorm)) return false;
    if (metalNorm && !parts.includes(metalNorm)) return false;

    // At least one filter must be active
    return Boolean(shapeNorm || metalNorm);
  }

  function getGallery(sectionId) {
    return (
      document.querySelector(`#MediaGallery-${sectionId} gallery-carousel`) ||
      document.querySelector(`#MainProduct-${sectionId} gallery-carousel`) ||
      document.querySelector('gallery-carousel')
    );
  }

  function filterDesktopGrid(gallery, shape, metal) {
    const galleryContainer = gallery.querySelector('.splide__gallery');
    if (!galleryContainer) return;

    // N04: populate desktop grids from the same archived media used on mobile.
    if (gallery.hasAttribute('data-n04-staged-gallery') && gallery.originalSlides?.length) {
      let selected = gallery.originalSlides.filter((slide) => altMatches(slide.dataset.mediaAlt, shape, metal));
      if (!selected.length) selected = gallery.originalSlides;
      const wanted = new Set(selected.map((slide) => slide.dataset.mediaId));
      [...galleryContainer.children].forEach((slide) => {
        if (!wanted.has(slide.dataset.mediaId) || slide.classList.contains('splide__slide--clone')) slide.remove();
      });
      selected.forEach((original, index) => {
        let slide = [...galleryContainer.children].find((item) => item.dataset.mediaId === original.dataset.mediaId);
        if (!slide) {
          slide = original.cloneNode(true);
          if (typeof gallery.initializeMediaInSlide === 'function') gallery.initializeMediaInSlide(slide);
        }
        if (galleryContainer.children[index] !== slide) galleryContainer.insertBefore(slide, galleryContainer.children[index] || null);
      });
    }

    const slides = galleryContainer.querySelectorAll('.splide__slide');
    let visibleCount = 0;

    slides.forEach((slide) => {
      const shouldShow = altMatches(slide.dataset.mediaAlt, shape, metal);
      if (shouldShow) {
        slide.style.display = '';
        slide.classList.remove('hidden-by-filter');
        visibleCount += 1;
      } else {
        slide.style.display = 'none';
        slide.classList.add('hidden-by-filter');
      }
    });

    const currentSlideElement = gallery.querySelector('.current-slide');
    const totalSlidesElement = gallery.querySelector('.total-slides');
    if (currentSlideElement && totalSlidesElement) {
      currentSlideElement.textContent = visibleCount > 0 ? '1' : '0';
      totalSlidesElement.textContent = String(visibleCount);
    }
  }

  function filterCarousel(gallery, shape, metal) {
    if (!gallery.main || !gallery.thumbnails || !gallery.originalSlides) return;

    const mainList = gallery.main.Components?.Elements?.list;
    const thumbList = gallery.thumbnails.Components?.Elements?.list;
    if (!mainList || !thumbList) return;

    let slidesToShow = gallery.originalSlides.filter((slide) =>
      altMatches(slide.dataset.mediaAlt, shape, metal)
    );
    let thumbsToShow = gallery.originalThumbnails.filter((slide) =>
      altMatches(slide.dataset.mediaAlt, shape, metal)
    );

    // Fallback: if nothing matches, keep current filtered set empty-safe → show all tagged, else all
    if (!slidesToShow.length) {
      slidesToShow = gallery.originalSlides;
      thumbsToShow = gallery.originalThumbnails;
    }

    // Keep already-filtered images in place when readiness checks run again.
    // Compare the rendered media, so real option or gallery changes still refresh.
    const hasSameMedia = (list, expected) => {
      const current = Array.from(list.children).filter((slide) =>
        slide.matches('.splide__slide:not(.splide__slide--clone)')
      );
      return current.length === expected.length && current.every((slide, index) =>
        slide.dataset.mediaId === expected[index].dataset.mediaId
      );
    };
    if (hasSameMedia(mainList, slidesToShow) && hasSameMedia(thumbList, thumbsToShow)) return;

    if (typeof gallery.saveMediaStates === 'function') gallery.saveMediaStates();

    if (typeof gallery.updateArrowVisibility === 'function') {
      gallery.updateArrowVisibility(thumbsToShow.length);
    }

    // Retain matching image nodes, especially the already-painted first image.
    const syncSlides = (list, expected, initializeMedia) => {
      const wanted = new Set(expected.map((slide) => slide.dataset.mediaId));
      Array.from(list.children).forEach((slide) => {
        if (!wanted.has(slide.dataset.mediaId) || slide.classList.contains('splide__slide--clone')) slide.remove();
      });
      expected.forEach((original, index) => {
        let slide = Array.from(list.children).find((item) => item.dataset.mediaId === original.dataset.mediaId);
        if (!slide) {
          slide = original.cloneNode(true);
          if (initializeMedia && typeof gallery.initializeMediaInSlide === 'function') gallery.initializeMediaInSlide(slide);
        }
        if (list.children[index] !== slide) list.insertBefore(slide, list.children[index] || null);
      });
    };
    syncSlides(mainList, slidesToShow, true);
    syncSlides(thumbList, thumbsToShow, false);

    try {
      gallery.main.refresh();
      gallery.thumbnails.refresh();
      if (slidesToShow.length > 0) {
        gallery.main.go(0);
        gallery.thumbnails.go(0);
        if (typeof gallery.updateSlideCounter === 'function') {
          gallery.updateSlideCounter(0);
        }
        if (typeof gallery.restoreMediaStates === 'function') {
          gallery.restoreMediaStates();
        }
      }
    } catch (error) {
      console.error('product-fake-attrs filter error:', error);
    }

    gallery.querySelectorAll('responsive-image').forEach((responsiveImg) => {
      if (typeof responsiveImg.connectedCallback === 'function') {
        responsiveImg.connectedCallback();
      } else if (typeof responsiveImg.init === 'function') {
        responsiveImg.init();
      }
    });

    if (typeof gallery.updateThumbnailARIA === 'function') {
      gallery.updateThumbnailARIA();
    }
  }

  function applyFilter(sectionId) {
    const state = getState(sectionId);
    const gallery = getGallery(sectionId);
    if (!gallery) return;
    if (gallery.hasAttribute('data-n04-staged-gallery') && !gallery.originalSlides?.length) {
      setTimeout(() => applyFilter(sectionId), 120);
      return;
    }

    const isDesktopGrid =
      gallery.hasAttribute('desktop-grid') && window.innerWidth >= 768;

    if (isDesktopGrid) {
      filterDesktopGrid(gallery, state.shape, state.metal);
    } else if (gallery.main && gallery.originalSlides?.length) {
      filterCarousel(gallery, state.shape, state.metal);
    } else {
      // Gallery not ready yet — retry shortly
      setTimeout(() => applyFilter(sectionId), 120);
    }
  }

  function setActive(picker, value) {
    let activeBtn = null;
    picker.querySelectorAll('[data-fake-attr-value]').forEach((btn) => {
      const isActive = normalize(btn.dataset.fakeAttrValue) === normalize(value);
      btn.classList.toggle('active', isActive);
      btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      const swatch = btn.querySelector('.product-fake-attrs__swatch');
      if (swatch) swatch.classList.toggle('active-swatch', isActive);
      const text = btn.querySelector('.product-fake-attrs__text');
      if (text) text.classList.toggle('active-text', isActive);
      if (isActive) activeBtn = btn;
    });

    const faceValue = picker.querySelector('[data-pfa-face-value]');
    if (faceValue && value) faceValue.textContent = value;

    const faceIcon = picker.querySelector('[data-pfa-face-icon]');
    const srcImg = activeBtn?.querySelector('img');
    if (faceIcon && srcImg) {
      const faceImg = faceIcon.querySelector('img');
      if (faceImg) {
        // Keep the customer's existing picker icon update behavior.
        const iconUrl =
          activeBtn.dataset.fakeAttrImage ||
          srcImg.currentSrc ||
          srcImg.src;

        faceImg.removeAttribute('srcset');
        faceImg.removeAttribute('sizes');

        ['data-src', 'data-srcset'].forEach((name) => {
          if (faceImg.hasAttribute(name)) {
            faceImg.setAttribute(name, iconUrl);
          }
        });

        faceIcon.querySelectorAll('source').forEach((source) => {
          source.setAttribute('srcset', iconUrl);
          if (source.hasAttribute('data-srcset')) {
            source.setAttribute('data-srcset', iconUrl);
          }
        });

        faceImg.loading = 'eager';
        faceImg.src = iconUrl;
        faceImg.alt = ''; // Adjacent text names the selected option.
      } else {
        faceIcon.innerHTML = '';
        faceIcon.appendChild(srcImg.cloneNode(true));
      }
    }
  }

  function syncCartProperty(picker, value) {
    const input = picker.querySelector('[data-fake-attr-property]');
    if (input) input.value = value || '';
  }

  function dispatchShapeChange(sectionId, value, imageUrl) {
    document.dispatchEvent(
      new CustomEvent('pfa:shape-change', {
        detail: { sectionId, shape: value, imageUrl: imageUrl || '' },
      })
    );
  }

  function getActiveShapeImage(picker, value) {
    const btn = [...picker.querySelectorAll('[data-fake-attr-value]')].find(
      (el) => normalize(el.dataset.fakeAttrValue) === normalize(value)
    );
    return btn?.dataset.fakeAttrImage || '';
  }

  function resolveInitial(picker) {
    const buttons = [...picker.querySelectorAll('[data-fake-attr-value]')];
    if (!buttons.length) return '';

    const attrType = picker.dataset.attrType;
    const state = getState(picker.dataset.sectionId);
    const productHandle = getPickerProductHandle(picker);
    const productKey = productHandle || `section:${picker.dataset.sectionId}`;

    // A section can be reused for a different product. Do not carry its state over.
    if (state.productKey !== productKey) {
      state.shape = '';
      state.metal = '';
      state.productKey = productKey;
    }

    const canUseEntrySelection =
      Boolean(entryProductHandle) && productHandle === entryProductHandle;
    const isSupportedAttribute = attrType === 'shape' || attrType === 'metal';
    const saved = isSupportedAttribute ? state[attrType] : '';
    const fromUrl = canUseEntrySelection && isSupportedAttribute
      ? entrySelection[attrType]
      : '';

    // First load: valid URL choice > template default > first button.
    // Reinitialization on this page: preserve the customer's current choice first.
    const candidates = [saved, fromUrl, picker.dataset.initialValue || ''];
    for (const requested of candidates) {
      if (!requested) continue;
      const match = buttons.find(
        (btn) => normalize(btn.dataset.fakeAttrValue) === normalize(requested)
      );
      if (match) return match.dataset.fakeAttrValue;
    }

    // Unknown query values are never inserted as new options or HTML.
    return buttons[0].dataset.fakeAttrValue;
  }

  function initPicker(picker) {
    if (picker.dataset.pfaReady === 'true') return;
    picker.dataset.pfaReady = 'true';

    const sectionId = picker.dataset.sectionId;
    const attrType = picker.dataset.attrType; // shape | metal
    const state = getState(sectionId);
    const initial = resolveInitial(picker);

    if (attrType === 'shape') state.shape = initial;
    if (attrType === 'metal') state.metal = initial;
    setActive(picker, initial);
    syncCartProperty(picker, initial);
    if (attrType === 'shape') {
      dispatchShapeChange(sectionId, initial, getActiveShapeImage(picker, initial));
    }

    picker.addEventListener('click', (event) => {
      const btn = event.target.closest('[data-fake-attr-value]');
      if (!btn || !picker.contains(btn)) return;
      event.preventDefault();

      const value = btn.dataset.fakeAttrValue;
      if (attrType === 'shape') state.shape = value;
      if (attrType === 'metal') state.metal = value;
      setActive(picker, value);
      syncCartProperty(picker, value);
      applyFilter(sectionId);
      if (attrType === 'shape') {
        dispatchShapeChange(sectionId, value, btn.dataset.fakeAttrImage || '');
      }
    });
  }

  function boot() {
    const pickers = document.querySelectorAll('product-fake-attr-picker');
    if (!pickers.length) return;

    const sectionIds = new Set();
    pickers.forEach((picker) => {
      initPicker(picker);
      sectionIds.add(picker.dataset.sectionId);
    });

    // Wait for gallery-carousel Splide mount
    sectionIds.forEach((sectionId) => {
      const run = () => applyFilter(sectionId);
      run();
      setTimeout(run, 300);
      setTimeout(run, 800);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Keep staged media and thumbnails aligned when crossing the gallery breakpoint.
  window.matchMedia('(min-width: 768px)').addEventListener('change', () => {
    requestAnimationFrame(() => {
      states.forEach((_, sectionId) => {
        if (getGallery(sectionId)?.hasAttribute('data-n04-staged-gallery')) applyFilter(sectionId);
      });
    });
  });

  document.addEventListener('shopify:section:load', boot);
})();